import React, {
  useEffect, useState, useCallback, useMemo, useRef
} from "react"
import { useSelector } from "react-redux"
import {
  addDays,
  startOfWeek,
  format,
  parseISO,
  setHours,
  setMinutes,
  parse
} from "date-fns"
import {
  DndContext,
  DragOverlay,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
  pointerWithin
} from "@dnd-kit/core"
import { toast } from "react-toastify"
import { StringParam, useQueryParam } from "use-query-params"

import FilterDropdown from "components/FilterDropdown"
import Loader from "components/Loader"
import {
  getSurveyorsForWeek,
  getSurveyorSchedule,
  updatePrimarySurveyor
} from "requesters/operationScheduleRequester"
import { updateSurveyorAssignmentDate } from "requesters/jobServiceRequester"
import {
  isTargetDateWithinWeek,
  validateRescheduleData,
  validateCrossSurveyorWithDateChangeData,
  getDragValidationErrorMessage,
  isJobServiceDraggable,
  isPublicHoliday
} from "./utils"

import { getBaseUrl } from "../../reducers"
import Calendar from "../../components/Calendar/Calendar"
import SurveyorScheduleBlock from "./SurveyorScheduleBlock"

// Surveyor type options matching the backend SurveyorTypes enumeration
const SURVEYOR_TYPE_OPTIONS = [
  ["Cadastral", "1"],
  ["Construction", "2"],
  ["3D", "3"],
  ["Scanner", "4"]
]

// Days of the week for the header
const WEEK_DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"]

// Loading states enum for better state management
const LOADING_STATES = {
  IDLE: "idle",
  LOADING: "loading",
  SUCCESS: "success",
  ERROR: "error"
}

/**
 * Custom collision detection that prioritizes day cells over surveyor blocks.
 * This ensures that when dropping on a day cell, it's detected as the target
 * instead of the parent surveyor block.
 *
 * @param {Object} args - Collision detection arguments from dnd-kit
 * @returns {Array} Array of collisions, prioritized by specificity
 */
const customCollisionDetection = args => {
  // Get all collisions using pointerWithin
  const collisions = pointerWithin(args)

  if (!collisions || collisions.length === 0) {
    return []
  }

  // Prioritize day cells (id starts with "day-") over surveyor blocks
  const dayCellCollisions = collisions.filter(c => c.id && c.id.toString().startsWith("day-"))
  const surveyorBlockCollisions = collisions.filter(c => c.id && c.id.toString().startsWith("surveyor-block-"))
  const otherCollisions = collisions.filter(c => (
    c.id && !c.id.toString().startsWith("day-") && !c.id.toString().startsWith("surveyor-block-")
  ))

  // Return day cells first, then surveyor blocks, then others
  const result = [...dayCellCollisions, ...surveyorBlockCollisions, ...otherCollisions]
  return result
}

/**
 * Parse a date string from URL parameter to a Date object
 * @param {string} dateStr - Date string in format "yyyy-MM-dd"
 * @returns {Date|null} Parsed date or null if invalid
 */
const parseDateFromUrl = dateStr => {
  if (!dateStr) return null
  try {
    const parsed = parse(dateStr, "yyyy-MM-dd", new Date())
    if (Number.isNaN(parsed.getTime())) return null
    return parsed
  } catch {
    return null
  }
}

/**
 * Get the Monday of the week containing the given date
 * @param {Date} date - Any date
 * @returns {Date} The Monday of that week
 */
const getWeekMonday = date => startOfWeek(date, { weekStartsOn: 1 })

/**
 * Extract error message from API error response
 * @param {Error|Object} error - Error object from API call
 * @returns {string} Human-readable error message
 */
const extractErrorMessage = error => {
  if (!error) return "An unexpected error occurred"

  // Handle axios error response
  if (error?.response?.data?.error) {
    return error.response.data.error
  }
  if (error?.response?.data?.errors?.[0]) {
    return error.response.data.errors[0]
  }

  // Handle direct error message
  if (error?.message) {
    return error.message
  }

  return "An unexpected error occurred"
}

export default function OperationSchedulePage() {
  const baseUrl = useSelector(getBaseUrl)

  // Add refs and handlers for synced horizontal scrolling across schedule containers
  const headerScrollRef = useRef(null)
  const bodyScrollRef = useRef(null)

  const handleHeaderScroll = useCallback((e) => {
    if (bodyScrollRef.current && bodyScrollRef.current.scrollLeft !== e.target.scrollLeft) {
      bodyScrollRef.current.scrollLeft = e.target.scrollLeft
    }
  }, [])

  const handleBodyScroll = useCallback((e) => {
    if (headerScrollRef.current && headerScrollRef.current.scrollLeft !== e.target.scrollLeft) {
      headerScrollRef.current.scrollLeft = e.target.scrollLeft
    }
  }, [])

  // URL state management for filters
  const [surveyorTypeParam, setSurveyorTypeParam] = useQueryParam("surveyor_type", StringParam)
  const [weekDateParam, setWeekDateParam] = useQueryParam("week", StringParam)

  // Derive weekStartDate directly from the URL param — single source of truth.
  // handleCalendarDateChange updates the URL which re-runs this memo.
  // Eliminates the two-effect bidirectional sync and its potential loop risk (Fix #5).
  const weekStartDate = useMemo(() => {
    const urlDate = parseDateFromUrl(weekDateParam)
    if (urlDate) return getWeekMonday(urlDate)
    return getWeekMonday(new Date())
  }, [weekDateParam])

  // Surveyor type filter from URL
  const surveyorTypeFilter = surveyorTypeParam || null

  // State for data with enhanced loading state management
  const [surveyors, setSurveyors] = useState([])
  const [publicHolidaysByDate, setPublicHolidaysByDate] = useState({})
  const [loadingState, setLoadingState] = useState(LOADING_STATES.IDLE)
  const [error, setError] = useState(null)
  const [retryCount, setRetryCount] = useState(0)

  // Per-surveyor schedule data fetched in parallel by the parent.
  // Shape: { [surveyorId]: scheduleDataObject | null }
  const [schedulesData, setSchedulesData] = useState({})

  // Per-surveyor refresh keys — only the affected surveyor's key increments
  // after a drag, preventing unrelated rows from unmounting & re-fetching.
  const [surveyorRefreshKeys, setSurveyorRefreshKeys] = useState({})

  // State for tracking active drag operation
  const [isUpdating, setIsUpdating] = useState(false)

  // Calculate week end date (Saturday) - memoized for performance
  const weekEndDate = useMemo(() => addDays(weekStartDate, 5), [weekStartDate])

  // Get the surveyor type name for display - memoized
  const surveyorTypeName = useMemo(() => (
    surveyorTypeFilter
      ? SURVEYOR_TYPE_OPTIONS.find(opt => opt[1] === surveyorTypeFilter)?.[0]
      : null
  ), [surveyorTypeFilter])

  // Derived loading state for backwards compatibility
  const loading = loadingState === LOADING_STATES.LOADING

  // Configure sensors for mouse and touch drag operations
  const mouseSensor = useSensor(MouseSensor, {
    // Require the mouse to move by 10 pixels before activating
    activationConstraint: {
      distance: 10
    }
  })

  const touchSensor = useSensor(TouchSensor, {
    // Press delay of 250ms, with tolerance of 5px of movement
    activationConstraint: {
      delay: 250,
      tolerance: 5
    }
  })

  const sensors = useSensors(mouseSensor, touchSensor)

  // Fetch surveyors for the selected week with retry logic
  const fetchSurveyors = useCallback(async () => {
    setLoadingState(LOADING_STATES.LOADING)
    setError(null)
    // Clear stale schedule data when the week / filter changes
    setSchedulesData({})

    try {
      const startDateStr = format(weekStartDate, "yyyy-MM-dd")
      const endDateStr = format(weekEndDate, "yyyy-MM-dd")

      const result = await getSurveyorsForWeek(
        baseUrl,
        startDateStr,
        endDateStr,
        surveyorTypeFilter
      )

      if (result.status === 200 && result.response?.data) {
        const fetchedSurveyors = result.response.data
        setSurveyors(fetchedSurveyors)
        // Extract public holidays from meta data
        const holidaysData = result.response.meta?.public_holidays_by_date || {}
        setPublicHolidaysByDate(holidaysData)
        setLoadingState(LOADING_STATES.SUCCESS)
        setRetryCount(0) // Reset retry count on success

        // Fetch all surveyor schedules in parallel (Fix #1 — eliminates waterfall)
        fetchAllSchedules(fetchedSurveyors, startDateStr, endDateStr)
      } else if (result.error) {
        // Check for 403 Forbidden (non-admin access)
        if (result.error?.response?.status === 403) {
          setError("Access denied. Admin privileges required.")
          setLoadingState(LOADING_STATES.ERROR)
        } else if (result.error?.response?.status === 400) {
          // Bad request - likely invalid parameters
          setError(extractErrorMessage(result.error) || "Invalid request parameters.")
          setLoadingState(LOADING_STATES.ERROR)
        } else {
          // Network or server error - allow retry
          setError("Failed to load surveyors. Please try again.")
          setLoadingState(LOADING_STATES.ERROR)
        }
        setSurveyors([])
        setPublicHolidaysByDate({})
      } else {
        setSurveyors([])
        setPublicHolidaysByDate({})
        setLoadingState(LOADING_STATES.SUCCESS)
      }
    } catch (err) {
      console.error("Error fetching surveyors:", err)
      setError("Failed to load surveyors. Please try again.")
      setLoadingState(LOADING_STATES.ERROR)
      setSurveyors([])
      setPublicHolidaysByDate({})
    }
  }, [baseUrl, weekStartDate, weekEndDate, surveyorTypeFilter])

  // Fetch all surveyor schedules in parallel (Fix #1 — eliminates the per-block waterfall).
  // Results are stored in schedulesData keyed by surveyor ID.
  const fetchAllSchedules = useCallback(async (surveyorList, startDateStr, endDateStr) => {
    if (!surveyorList?.length) return

    const results = await Promise.all(
      surveyorList.map(surveyor =>
        getSurveyorSchedule(baseUrl, surveyor.id, startDateStr, endDateStr)
          .then(result => ({ surveyorId: surveyor.id, data: result.response?.data || null }))
          .catch(() => ({ surveyorId: surveyor.id, data: null }))
      )
    )

    const dataMap = {}
    results.forEach(({ surveyorId, data }) => { dataMap[surveyorId] = data })
    setSchedulesData(dataMap)
  }, [baseUrl])

  // Re-fetch a single surveyor's schedule after a drag operation (Fix #3).
  // Only the affected surveyor's refresh key is bumped; all other rows keep
  // their mounted state and cached data intact.
  const refreshSurveyorSchedule = useCallback(async surveyorId => {
    const startDateStr = format(weekStartDate, "yyyy-MM-dd")
    const endDateStr = format(weekEndDate, "yyyy-MM-dd")

    try {
      const result = await getSurveyorSchedule(baseUrl, surveyorId, startDateStr, endDateStr)
      const data = result.response?.data || null

      setSchedulesData(prev => ({ ...prev, [surveyorId]: data }))
      // Bump only this surveyor's key to force the row to pick up new data
      setSurveyorRefreshKeys(prev => ({ ...prev, [surveyorId]: (prev[surveyorId] || 0) + 1 }))
    } catch (err) {
      console.error("Error refreshing surveyor schedule:", err)
    }
  }, [baseUrl, weekStartDate, weekEndDate])

  // Retry handler for failed requests
  const handleRetry = useCallback(() => {
    if (retryCount < 3) {
      setRetryCount(prev => prev + 1)
      fetchSurveyors(true)
    } else {
      toast.error("Maximum retry attempts reached. Please refresh the page.")
    }
  }, [fetchSurveyors, retryCount])

  // Fetch surveyors on mount and when filters change
  useEffect(() => {
    fetchSurveyors()
  }, [fetchSurveyors])

  // Handle surveyor type filter change - update URL param
  const handleSurveyorTypeChange = value => {
    setSurveyorTypeParam(value || undefined)
  }

  // Handle calendar date change — just update the URL param.
  // weekStartDate is derived from the param via useMemo (Fix #5).
  const handleCalendarDateChange = useCallback(date => {
    setWeekDateParam(format(getWeekMonday(date), "yyyy-MM-dd"))
  }, [setWeekDateParam])

  // Handle cross-surveyor reassignment for a single job service
  const handleSingleCrossSurveyorReassignment = async (jobService, targetSurveyorId) => {
    const jobServiceId = jobService.id

    try {
      // Use the operation_schedules API endpoint for cross-surveyor reassignment
      const result = await updatePrimarySurveyor(baseUrl, jobServiceId, targetSurveyorId)

      if (result.error) {
        // Extract error message from response
        const errorMessage = extractErrorMessage(result.error)
        throw new Error(errorMessage)
      }

      return { success: true, jobServiceId }
    } catch (err) {
      const errorMessage = extractErrorMessage(err)
      return { success: false, jobServiceId, error: errorMessage }
    }
  }

  // Handle cross-surveyor reassignment (supports both single and grouped job services)
  // Update primary surveyor when moving between surveyors
  const handleCrossSurveyorReassignment = async (jobService, targetSurveyorId, isGrouped, groupedServices, sourceSurveyorId) => {
    // sourceSurveyorId comes from drag data and is always reliable.
    // jobService.primary_surveyor_id is NOT serialized by the API, so we never read it here.
    setIsUpdating(true)

    try {
      // Determine which job services to update
      const servicesToUpdate = isGrouped && groupedServices?.length > 0
        ? groupedServices
        : [jobService]

      // Update all job services in the group
      const results = await Promise.all(
        servicesToUpdate.map(service => handleSingleCrossSurveyorReassignment(service, targetSurveyorId))
      )

      // Check for failures
      const failures = results.filter(r => !r.success)
      const successes = results.filter(r => r.success)

      if (failures.length > 0 && successes.length === 0) {
        // All failed - show error
        toast.error(failures[0].error || "Failed to reassign surveyor. Please try again.")
        return
      }

      if (failures.length > 0 && successes.length > 0) {
        // Partial success — groups are now split; refresh both surveyors immediately
        toast.warning(`${successes.length} job service(s) reassigned, ${failures.length} failed. The jobs are no longer grouped together.`)
        await refreshSurveyorSchedule(targetSurveyorId)
        if (sourceSurveyorId && sourceSurveyorId !== targetSurveyorId) {
          await refreshSurveyorSchedule(sourceSurveyorId)
        }
      } else {
        // All succeeded
        const message = servicesToUpdate.length > 1
          ? `${servicesToUpdate.length} job services reassigned successfully`
          : "Job service reassigned successfully"
        toast.success(message)

        // Refresh only the two affected surveyor rows — await so data lands before isUpdating clears
        await refreshSurveyorSchedule(targetSurveyorId)
        if (sourceSurveyorId && sourceSurveyorId !== targetSurveyorId) {
          await refreshSurveyorSchedule(sourceSurveyorId)
        }
      }
    } catch (err) {
      // Show error toast on failure
      const errorMessage = extractErrorMessage(err)
      toast.error(errorMessage)
      console.error("Reassignment error:", err)
    } finally {
      setIsUpdating(false)
    }
  }

  const calculateServiceOriginalDate = (service, fallbackDate) => {
    const bookingTimeStr = service.booking_time || service.bookingTime
    const bookingDateStr = service.booking_date || service.bookingDate

    if (!bookingDateStr) {
      // Use fallback date if no booking date available
      return fallbackDate
    }

    try {
      // Check if booking_time is an ISO timestamp (contains 'T' or 'Z')
      if (bookingTimeStr && (bookingTimeStr.includes("T") || bookingTimeStr.includes("Z"))) {
        // Parse as full ISO timestamp - this will be in the timezone sent by backend
        const serviceDate = parseISO(bookingTimeStr)

        if (Number.isNaN(serviceDate.getTime())) {
          throw new Error(`Invalid ISO timestamp: ${bookingTimeStr}`)
        }

        return serviceDate
      }

      // Legacy format: booking_time is "HH:MM" string, booking_date is "YYYY-MM-DD"
      const [hours, minutes] = (bookingTimeStr || "09:00").split(":").map(Number)
      const serviceDate = parseISO(bookingDateStr)

      if (Number.isNaN(serviceDate.getTime())) {
        throw new Error(`Invalid booking date: ${bookingDateStr}`)
      }

      serviceDate.setHours(hours || 9, minutes || 0, 0, 0)
      return serviceDate
    } catch (error) {
      console.error("Date parsing error:", error)
      return fallbackDate
    }
  }

  const calculateServiceNewDate = (service, originalDate, targetDate) => {
    // Preserve the time component from original date
    const newDate = new Date(targetDate)
    newDate.setHours(
      originalDate.getHours(),
      originalDate.getMinutes(),
      0,
      0
    )
    return newDate
  }

  // Handle cross-surveyor with date change for a single job service
  const handleSingleCrossSurveyorWithDateChange = async (jobService, targetSurveyorId, originalDate, newDate) => {
    const jobServiceId = jobService.id

    try {
      // Step 1: Update primary surveyor
      const surveyorResult = await updatePrimarySurveyor(baseUrl, jobServiceId, targetSurveyorId)

      if (surveyorResult.error) {
        throw new Error(`Surveyor update failed: ${extractErrorMessage(surveyorResult.error)}`)
      }

      // Step 2: Update booking date
      // Send times as ISO strings - backend will handle timezone conversion
      const dateResult = await updateSurveyorAssignmentDate(baseUrl, jobServiceId, {
        booking_time: originalDate.toISOString(),
        new_booking_time: newDate.toISOString()
      })

      if (dateResult.error) {
        // Date update failed - attempt to revert surveyor change
        try {
          const originalSurveyorId = jobService.primary_surveyor_id || jobService.primarySurveyorId
          if (originalSurveyorId) {
            await updatePrimarySurveyor(baseUrl, jobServiceId, originalSurveyorId)
          }
        } catch (revertError) {
          console.error("Failed to revert surveyor change:", revertError)
        }

        throw new Error(`Date update failed: ${extractErrorMessage(dateResult.error)}`)
      }

      return {
        success: true,
        jobServiceId,
        surveyorChanged: true,
        dateChanged: true
      }
    } catch (err) {
      const errorMessage = extractErrorMessage(err)
      return {
        success: false,
        jobServiceId,
        error: errorMessage,
        surveyorChanged: false,
        dateChanged: false
      }
    }
  }

  // Handle results for cross-surveyor with date change operations
  const handleCrossSurveyorWithDateChangeResults = async (results, totalCount, sourceSurveyorId, targetSurveyorId) => {
    const failures = results.filter(r => !r.success)
    const successes = results.filter(r => r.success)

    if (failures.length > 0 && successes.length === 0) {
      // All failed
      const errorMessage = failures[0].error || "Failed to update surveyor and date"
      toast.error(errorMessage)
    } else if (failures.length > 0 && successes.length > 0) {
      // Partial success - jobs are now unlinked/ungrouped
      toast.warning(`${successes.length} job service(s) updated (surveyor and date), ${failures.length} failed. The jobs are no longer grouped together.`)
    } else {
      // All succeeded
      const message = totalCount > 1
        ? `${totalCount} job services moved to new surveyor and date successfully`
        : "Job service moved to new surveyor and date successfully"
      toast.success(message)
    }

    // Refresh only the affected surveyor rows — await so data lands before isUpdating clears
    await refreshSurveyorSchedule(targetSurveyorId)
    if (sourceSurveyorId && sourceSurveyorId !== targetSurveyorId) {
      await refreshSurveyorSchedule(sourceSurveyorId)
    }
  }

  // Handle errors for cross-surveyor with date change operations
  const handleCrossSurveyorWithDateChangeError = error => {
    const errorMessage = extractErrorMessage(error)
    toast.error(`Combined operation failed: ${errorMessage}`)
    console.error("Cross-surveyor with date change error:", error)
  }

  // Handle cross-surveyor with date change (supports both single and grouped job services)
  const handleCrossSurveyorWithDateChange = async (jobService, targetSurveyorId, originalDate, newDate, isGrouped, groupedServices, sourceSurveyorId) => {
    // sourceSurveyorId comes from drag data and is always reliable.
    // jobService.primary_surveyor_id is NOT serialized by the API, so we never read it here.
    setIsUpdating(true)

    try {
      // Determine which job services to update
      const servicesToUpdate = isGrouped && groupedServices?.length > 0
        ? groupedServices
        : [jobService]

      // Process each service with both surveyor and date updates
      const results = await Promise.all(
        servicesToUpdate.map(service => handleSingleCrossSurveyorWithDateChange(service, targetSurveyorId, originalDate, newDate))
      )

      // Process results and show appropriate feedback
      // Pass surveyor IDs so the helper refreshes only affected rows
      await handleCrossSurveyorWithDateChangeResults(results, servicesToUpdate.length, sourceSurveyorId, targetSurveyorId)
    } catch (err) {
      handleCrossSurveyorWithDateChangeError(err)
    } finally {
      setIsUpdating(false)
    }
  }

  // Handle same-surveyor reschedule for a single job service
  const handleSingleSameSurveyorReschedule = async (jobService, originalBookingTime, newBookingTime) => {
    const jobServiceId = jobService.id

    try {
      // Send times as ISO strings - backend will handle timezone conversion
      const result = await updateSurveyorAssignmentDate(baseUrl, jobServiceId, {
        booking_time: originalBookingTime.toISOString(),
        new_booking_time: newBookingTime.toISOString()
      })

      if (result.error) {
        const errorMessage = extractErrorMessage(result.error)
        throw new Error(errorMessage)
      }

      return { success: true, jobServiceId }
    } catch (err) {
      const errorMessage = extractErrorMessage(err)
      return { success: false, jobServiceId, error: errorMessage }
    }
  }

  const handleRescheduleResults = async (results, totalCount, surveyorId) => {
    const failures = results.filter(r => !r.success)
    const successes = results.filter(r => r.success)

    if (failures.length > 0 && successes.length === 0) {
      // All failed
      const errorMessage = failures[0].error || "Failed to reschedule job services"
      toast.error(errorMessage)
    } else if (failures.length > 0 && successes.length > 0) {
      // Partial success - jobs are now unlinked/ungrouped
      toast.warning(`${successes.length} job service(s) rescheduled, ${failures.length} failed. The jobs are no longer grouped together.`)
    } else {
      // All succeeded
      const message = totalCount > 1
        ? `${totalCount} job services rescheduled successfully`
        : "Job service rescheduled successfully"
      toast.success(message)
    }

    // Refresh only the affected surveyor's row — await so data lands before isUpdating clears
    if (surveyorId) await refreshSurveyorSchedule(surveyorId)
  }

  const handleRescheduleError = error => {
    const errorMessage = extractErrorMessage(error)
    toast.error(`Reschedule failed: ${errorMessage}`)
    console.error("Reschedule error:", error)
  }

  // Handle same-surveyor reschedule (supports both single and grouped job services)
  // Update booking date when moving to different days
  // The surveyorId param comes from drag data (sourceSurveyorId) and is always reliable.
  // jobService.primary_surveyor_id is NOT serialized by the API, so we pass it in explicitly.
  const handleSameSurveyorReschedule = async (jobService, originalBookingTime, newBookingTime, isGrouped, groupedServices, surveyorId) => {
    setIsUpdating(true)

    try {
      // Determine which job services to update
      const servicesToUpdate = isGrouped && groupedServices?.length > 0
        ? groupedServices
        : [jobService]

      // Update all job services in the group
      const results = await Promise.all(
        servicesToUpdate.map(service => {
          // Calculate service-specific dates
          const serviceOriginalDate = calculateServiceOriginalDate(service, originalBookingTime)
          const serviceNewDate = calculateServiceNewDate(service, serviceOriginalDate, newBookingTime)

          return handleSingleSameSurveyorReschedule(service, serviceOriginalDate, serviceNewDate)
        })
      )

      // Process results and refresh only this surveyor's row
      await handleRescheduleResults(results, servicesToUpdate.length, surveyorId)
    } catch (err) {
      handleRescheduleError(err)
    } finally {
      setIsUpdating(false)
    }
  }

  // Active drag item state — used to render the DragOverlay ghost.
  // Storing only the minimal data needed (address + price) keeps renders cheap.
  const [activeDragItem, setActiveDragItem] = useState(null)

  // Handle drag start event
  const handleDragStart = useCallback(event => {
    // Prevent drag start if already updating
    if (isUpdating) {
      toast.warning(getDragValidationErrorMessage("CONCURRENT_UPDATE"))
      return
    }
    // Capture lightweight data for the DragOverlay ghost
    const dragData = event.active?.data?.current
    if (dragData?.jobService) {
      const { jobService } = dragData
      setActiveDragItem({
        address: jobService.address || "Job Service",
        price: parseFloat(jobService.total_cost || jobService.totalCost || jobService.price) || 0
      })
    }
  }, [isUpdating])

  // Clear the overlay ghost after drag ends or is cancelled
  const clearActiveDragItem = useCallback(() => {
    setActiveDragItem(null)
  }, [])

  // Handle drag end event for rescheduling job services and reassigning surveyors.
  // Wrapped in useCallback so DndContext always receives a stable function reference (Fix #4).
  const handleDragEnd = useCallback(async event => {
    const { active, over } = event

    // If no valid drop target, show error message
    if (!over) {
      toast.error(getDragValidationErrorMessage("MISSING_DROP_TARGET"))
      return
    }

    // Don't process if already updating (concurrency control)
    if (isUpdating) {
      toast.warning(getDragValidationErrorMessage("CONCURRENT_UPDATE"))
      return
    }

    // Extract data from the draggable and droppable
    const dragData = active.data.current
    let dropData = over.data.current

    // Ensure we have valid data
    if (!dragData || !dropData) {
      toast.error(getDragValidationErrorMessage("MISSING_DRAG_DATA"))
      return
    }

    // If the over.id starts with "day-", extract the surveyorId and dayIndex from the ID
    // This is a fallback in case the dropData doesn't have the expected properties
    const overId = over.id?.toString() || ""
    if (overId.startsWith("day-") && (dropData.dayIndex === undefined || !dropData.date)) {
      // Format: "day-{surveyorId}-{dayIndex}"
      const parts = overId.split("-")
      if (parts.length >= 3) {
        const extractedSurveyorId = parseInt(parts[1], 10)
        const extractedDayIndex = parseInt(parts[2], 10)

        // Update dropData with extracted values if missing
        dropData = {
          ...dropData,
          surveyorId: dropData.surveyorId ?? extractedSurveyorId,
          dayIndex: dropData.dayIndex ?? extractedDayIndex,
          type: dropData.type || "day-cell"
        }
      }
    }

    const {
      jobService, surveyorId: sourceSurveyorId, isGrouped, groupedServices
    } = dragData
    const {
      surveyorId: targetSurveyorId, dayIndex, date: targetDate, type: dropType
    } = dropData

    // Validate that the job service is draggable
    if (!isJobServiceDraggable(jobService)) {
      // Check if it's because the job is checked in
      const isCheckedIn = jobService.checked_in_at || jobService.checkedInAt
      if (isCheckedIn) {
        toast.error("Cannot reschedule this job - work has already started (checked in)")
      } else {
        toast.error(getDragValidationErrorMessage("NON_DRAGGABLE_SERVICE"))
      }
      return
    }

    // Get the original booking date and time
    // The API now returns booking_time as full ISO timestamp for timezone conversion
    const bookingTimeStr = jobService.booking_time || jobService.bookingTime
    const bookingDateStr = jobService.booking_date || jobService.bookingDate

    if (!bookingDateStr) {
      toast.error(getDragValidationErrorMessage("MISSING_BOOKING_DATE"))
      return
    }

    // Parse the booking date and time
    let originalDate
    try {
      // Check if booking_time is an ISO timestamp (contains 'T' or 'Z')
      if (bookingTimeStr && (bookingTimeStr.includes("T") || bookingTimeStr.includes("Z"))) {
        // Parse as full ISO timestamp
        originalDate = parseISO(bookingTimeStr)
      } else {
        // Legacy format: bookingTimeStr is "HH:MM", bookingDateStr is "YYYY-MM-DD"
        const [hours, minutes] = (bookingTimeStr || "09:00").split(":").map(Number)
        originalDate = parseISO(bookingDateStr)
        originalDate.setHours(hours || 9, minutes || 0, 0, 0)
      }

      if (Number.isNaN(originalDate.getTime())) {
        throw new Error("Invalid date")
      }
    } catch (error) {
      console.error("Error parsing booking date/time:", error)
      toast.error(getDragValidationErrorMessage("MISSING_BOOKING_DATE"))
      return
    }

    // Calculate original day index (0 = Monday, 5 = Saturday)
    const originalDayIndex = originalDate.getDay() === 0 ? 6 : originalDate.getDay() - 1

    // Calculate the target date from dayIndex if not provided
    let effectiveTargetDate = targetDate
    if (!effectiveTargetDate && dayIndex !== undefined) {
      // We need to calculate the target date from the weekStartDate
      // This requires access to weekStartDate, which we have in scope
      effectiveTargetDate = addDays(weekStartDate, dayIndex)
    }

    // Determine operation type based on surveyor and day differences
    const isSameSurveyor = sourceSurveyorId === targetSurveyorId
    const isSameDay = originalDayIndex === dayIndex

    if (isSameSurveyor && isSameDay) {
      // Scenario 1: Same surveyor, same day → No-op
      toast.info(getDragValidationErrorMessage("NO_OP_OPERATION"))
      return
    }

    if (isSameSurveyor && !isSameDay) {
      // Scenario 2: Same surveyor, different day → handleSameSurveyorReschedule

      // If dropped on surveyor block (not a day cell), do nothing for same surveyor
      // We need a specific day cell to reschedule
      if (dropType === "surveyor-block") {
        return
      }

      // Validate that we have the required data for same-surveyor reschedule
      if (dayIndex === undefined) {
        toast.error(getDragValidationErrorMessage("INVALID_TARGET_DATE"))
        return
      }

      if (!effectiveTargetDate) {
        toast.error(getDragValidationErrorMessage("INVALID_TARGET_DATE"))
        return
      }

      // Validate that target date is within current week
      if (!isTargetDateWithinWeek(effectiveTargetDate, weekStartDate, weekEndDate)) {
        toast.error(getDragValidationErrorMessage("TARGET_DATE_OUT_OF_WEEK"))
        return
      }

      // Check if target date is a public holiday
      if (isPublicHoliday(effectiveTargetDate, publicHolidaysByDate)) {
        toast.error("Cannot schedule job services on public holidays")
        return
      }

      // Calculate the new booking time
      // Keep the same time of day, just change the date
      const originalHours = originalDate.getHours()
      const originalMinutes = originalDate.getMinutes()

      let newBookingTime = new Date(effectiveTargetDate)
      newBookingTime = setHours(newBookingTime, originalHours)
      newBookingTime = setMinutes(newBookingTime, originalMinutes)

      // Final validation of reschedule data
      const validation = validateRescheduleData(jobService, originalDate, newBookingTime)
      if (!validation.success) {
        if (validation.isNoOp) {
          toast.info(getDragValidationErrorMessage("NO_OP_OPERATION"))
        } else {
          toast.error(validation.error)
        }
        return
      }

      await handleSameSurveyorReschedule(jobService, originalDate, newBookingTime, isGrouped, groupedServices, sourceSurveyorId)
      return
    }

    if (!isSameSurveyor && isSameDay) {
      // Scenario 3: Different surveyor, same day → handleCrossSurveyorReassignment
      await handleCrossSurveyorReassignment(jobService, targetSurveyorId, isGrouped, groupedServices, sourceSurveyorId)
      return
    }

    // Scenario 4: Different surveyor, different day → handleCrossSurveyorWithDateChange (NEW)

    // Validate that we have the required data for combined operation
    if (dayIndex === undefined) {
      toast.error(getDragValidationErrorMessage("INVALID_TARGET_DATE"))
      return
    }

    if (!effectiveTargetDate) {
      toast.error(getDragValidationErrorMessage("INVALID_TARGET_DATE"))
      return
    }

    // Validate that target date is within current week
    if (!isTargetDateWithinWeek(effectiveTargetDate, weekStartDate, weekEndDate)) {
      toast.error(getDragValidationErrorMessage("TARGET_DATE_OUT_OF_WEEK"))
      return
    }

    // Check if target date is a public holiday
    if (isPublicHoliday(effectiveTargetDate, publicHolidaysByDate)) {
      toast.error("Cannot schedule job services on public holidays")
      return
    }

    // Calculate the new booking time
    // Keep the same time of day, just change the date
    const originalHours = originalDate.getHours()
    const originalMinutes = originalDate.getMinutes()

    let newBookingTime = new Date(effectiveTargetDate)
    newBookingTime = setHours(newBookingTime, originalHours)
    newBookingTime = setMinutes(newBookingTime, originalMinutes)

    // Validate the combined operation data
    const combinedValidation = validateCrossSurveyorWithDateChangeData(
      jobService,
      targetSurveyorId,
      originalDate,
      newBookingTime
    )
    if (!combinedValidation.success) {
      toast.error(getDragValidationErrorMessage("COMBINED_OPERATION_VALIDATION_FAILED", {
        message: combinedValidation.error
      }))
      return
    }

    await handleCrossSurveyorWithDateChange(jobService, targetSurveyorId, originalDate, newBookingTime, isGrouped, groupedServices, sourceSurveyorId)
  }, [
    isUpdating, weekStartDate, weekEndDate, publicHolidaysByDate,
    handleSameSurveyorReschedule, handleCrossSurveyorReassignment, handleCrossSurveyorWithDateChange
  ])

  // Handle drag cancel event — clear the overlay ghost
  const handleDragCancel = useCallback(() => {
    clearActiveDragItem()
  }, [clearActiveDragItem])

  // Stable combined drag-end: clear overlay then process drop.
  // Defined as useCallback so DndContext always receives the same reference.
  const handleDragEndWithClear = useCallback(event => {
    clearActiveDragItem()
    handleDragEnd(event)
  }, [clearActiveDragItem, handleDragEnd])

  // Memoized week days with dates for header
  const weekDaysWithDates = useMemo(() => (
    WEEK_DAYS.map((day, idx) => ({
      day,
      date: addDays(weekStartDate, idx),
      formattedDate: format(addDays(weekStartDate, idx), "d MMM")
    }))
  ), [weekStartDate])

  return (
    <div className="container-fluid mb-5">
      <div className="container-inner">
        <div
          style={{
            position: "sticky",
            top: 0,
            zIndex: 200,
            paddingBottom: "0",
            paddingTop: "20px",
            backgroundColor: "white"
          }}>
          {/* Header */}
          <div className="d-md-flex justify-content-between align-items-center header-tabs-container">
            <div className="header-tabs">
              <h1>Operation Schedule</h1>
            </div>
          </div>

          {/* Filters */}
          <div className="table-listing-filters mb-2 d-md-flex justify-content-between">
            <div>
              <div className="btn-group mr-2">
                <FilterDropdown
                  title="Surveyor Type"
                  filteredName={surveyorTypeName}
                  setFilter={handleSurveyorTypeChange}
                  options={SURVEYOR_TYPE_OPTIONS} />
              </div>
            </div>

            <Calendar
              startDate={weekStartDate}
              endDate={weekEndDate}
              calendarDate={weekStartDate}
              setCalendarDate={handleCalendarDateChange} />
          </div>

          {/* Schedule Grid Header — kept inside sticky block so it scrolls with filters */}
          {!error && (
            <div 
              className="operation-schedule-header-wrapper"
              ref={headerScrollRef}
              onScroll={handleHeaderScroll}
              style={{ overflowX: 'auto', msOverflowStyle: 'none', scrollbarWidth: 'none' }}>
              <table className="table table-bordered operation-schedule-table mb-0">
                <thead>
                  <tr className="schedule-header-row">
                    <th className="surveyor-name-col">Surveyor Name</th>
                    {weekDaysWithDates.map(({ day, formattedDate }) => (
                      <th key={day} className="day-col">
                        <div>{day}</div>
                        <div className="day-date">{formattedDate}</div>
                      </th>
                    ))}
                    <th className="total-col">Total</th>
                  </tr>
                </thead>
              </table>
            </div>
          )}
        </div>

        {/* Error State */}
        {error && (
          <div className="alert alert-danger d-flex justify-content-between align-items-center" role="alert">
            <span>{error}</span>
            {loadingState === LOADING_STATES.ERROR && retryCount < 3 && (
              <button
                type="button"
                className="btn btn-outline-danger btn-sm"
                onClick={handleRetry}
                disabled={loading}>
                Retry
              </button>
            )}
          </div>
        )}

        {/* Schedule Grid Body */}
        {!error && (
          <div 
            className="operation-schedule-container"
            ref={bodyScrollRef}
            onScroll={handleBodyScroll}>

            {/* Loading State */}
            {loading && <Loader />}

            {/* Empty State */}
            {!loading && surveyors.length === 0 && (
              <div className="text-center py-4">
                <p className="text-muted">No surveyors found for the selected week and filters.</p>
              </div>
            )}

            {/* Surveyors List */}
            {!loading && surveyors.length > 0 && (
              <DndContext
                sensors={sensors}
                collisionDetection={customCollisionDetection}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEndWithClear}
                onDragCancel={handleDragCancel}>
                <div className="surveyors-schedule-list">
                  {surveyors.map(surveyor => (
                    <SurveyorScheduleBlock
                      key={surveyor.id}
                      surveyor={surveyor}
                      weekStartDate={weekStartDate}
                      weekEndDate={weekEndDate}
                      publicHolidaysByDate={publicHolidaysByDate}
                      scheduleData={surveyor.id in schedulesData ? schedulesData[surveyor.id] : null}
                      refreshTrigger={surveyorRefreshKeys[surveyor.id] || 0}
                      onRefresh={() => refreshSurveyorSchedule(surveyor.id)}
                      isUpdating={isUpdating} />
                  ))}
                </div>

                {/* DragOverlay renders the ghost card in a portal above all content.
                    This isolates drag-move renders to only the overlay, not the full tree. */}
                <DragOverlay dropAnimation={{
                  duration: 200,
                  easing: "cubic-bezier(0.18, 0.67, 0.6, 1.22)"
                }}>
                  {activeDragItem ? (
                    <div style={{
                      backgroundColor: "#3b6dc9",
                      color: "white",
                      borderRadius: "6px",
                      padding: "8px 10px",
                      fontSize: "12px",
                      fontWeight: 500,
                      boxShadow: "0 8px 24px rgba(0,0,0,0.25)",
                      minWidth: "120px",
                      maxWidth: "200px",
                      opacity: 0.95,
                      cursor: "grabbing",
                      pointerEvents: "none"
                    }}>
                      <div style={{ fontWeight: 700, marginBottom: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {activeDragItem.address.length > 30
                          ? `${activeDragItem.address.substring(0, 30)}…`
                          : activeDragItem.address}
                      </div>
                      {activeDragItem.price > 0 && (
                        <div style={{ opacity: 0.85 }}>${activeDragItem.price.toFixed(0)}</div>
                      )}
                    </div>
                  ) : null}
                </DragOverlay>
              </DndContext>
            )}

            {/* Loading overlay during update operations */}
            {isUpdating && (
              <div className="update-overlay">
                <div className="update-overlay-content">
                  <Loader />
                  <span>Updating schedule...</span>
                  <div className="update-overlay-message">
                    Please wait while we process your changes
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
